import { useCallback, useEffect, useState } from 'react';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import DateRangeSelector, { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import {
  AuditOperationsApiProvider,
  useAuditOperationsApi,
} from '../Services/AuditOperationsApiProvider';
import { useChatAnalyticsApi } from '../Services/ChatAnalyticsApiProvider';
import { useAuth } from '../Contexts/AuthContext';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

const resolveRange = (start, end) =>
  start ? { start_date: start, end_date: end } : getDateRangeByPeriod('today');

const AUDIT_COLUMNS = [
  { name: 'ID', selector: (row) => row.key, sortable: true },
  { name: 'User Key', selector: (row) => row.user_key, sortable: true },
  { name: 'Operation Type', selector: (row) => row.operation_type, sortable: true },
  { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
  { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
];

const LLM_COLUMNS = [
  { name: 'Model', selector: (row) => row.model_name, sortable: true },
  { name: 'Total Tokens', selector: (row) => row.total_tokens, sortable: true },
  { name: 'Input Tokens', selector: (row) => row.input_tokens, sortable: true },
  { name: 'Output Tokens', selector: (row) => row.output_tokens, sortable: true },
];

function AuditOperationsHub({ canEdit }) {
  const { getAuditOperations, getAuditOperationTypes, exportAuditOperations } =
    useAuditOperationsApi();
  const { organizationId } = useParams();
  const { getTokenAnalyticsByLLM, getTokenAnalytics } = useChatAnalyticsApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const [searchOptions, setSearchOptions] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const hasOrganizationsPermission = authStatus?.permissions?.includes('all:organizations');

  const [tokenAnalytics, setTokenAnalytics] = useState(null);
  const [tokenAnalyticsByLLM, setTokenAnalyticsByLLM] = useState([]);
  const [totalTokenCount, setTotalTokenCount] = useState(0);

  const [tokenStartDate, setTokenStartDate] = useState();
  const [tokenEndDate, setTokenEndDate] = useState();
  const [llmTokenStartDate, setLlmTokenStartDate] = useState();
  const [llmTokenEndDate, setLlmTokenEndDate] = useState();

  const tableTitle = 'Audit Operations';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const tableTokensTitle = 'LLM Analytics';
  const { persistedPageSize: persistedTokensPageSize, persistedSkip: persistedTokensSkip } =
    getTablePaginationFromStorage(formatTableTitle(tableTokensTitle));

  const handlePeriodChange = useCallback((period) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const fetchAuditOperationTypes = async () => {
    try {
      return await getAuditOperationTypes();
    } catch (error) {
      console.error('Error fetching audit operation types:', error);
    }
  };

  const fetchAuditOperations = async (skip, limit, type = selectedTypes) => {
    try {
      setLoading(true);
      const { start_date, end_date } = resolveRange(startDate, endDate);
      const response = await getAuditOperations(skip, limit, start_date, end_date, type);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
    } catch (error) {
      console.error('Error fetching audit operations:', error);
      setError('Failed to load audit operations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditOperations(persistedSkip, persistedPageSize);
  }, [startDate, endDate]);

  useEffect(() => {
    const loadTypes = async () => {
      const types = await fetchAuditOperationTypes();
      if (!types) return;
      const sortedTypes = [...types].sort((a, b) => a.localeCompare(b));
      const options = sortedTypes.map((type) => ({ label: type, value: type }));
      setSearchOptions(options);
    };
    loadTypes();
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/audit-operations/${row.key}`);
  };

  const handleTypeChange = async (skip, limit, type) => {
    const nextTypes = Array.isArray(type) ? type : type ? [type] : [];

    setSelectedTypes(nextTypes);
    await fetchAuditOperations(skip, limit, nextTypes);
  };

  const handleExport = async () => {
    try {
      const { start_date, end_date } = resolveRange(startDate, endDate);
      // якщо використовуєш мультиселект типів — передай сюди масив або null
      await exportAuditOperations(start_date, end_date, selectedTypes ?? null);
    } catch (error) {
      console.error('Error exporting audit operations:', error);
    }
  };

  const fetchTokenAnalyticsByLLM = async (skip, limit) => {
    const { start_date, end_date } = resolveRange(llmTokenStartDate, llmTokenEndDate);

    try {
      const response = await getTokenAnalyticsByLLM(start_date, end_date, skip, limit);
      setTokenAnalyticsByLLM(response.items || []);
      setTotalTokenCount(response.totalCount || 0);
    } catch (error) {
      console.error('Error fetching token analytics:', error);
    }
  };

  useEffect(() => {
    if (hasOrganizationsPermission) {
      fetchTokenAnalyticsByLLM(persistedTokensSkip, persistedTokensPageSize);
    }
  }, [llmTokenStartDate, llmTokenEndDate, getTokenAnalyticsByLLM, authStatus?.permissions]);

  const fetchTokenAnalytics = async () => {
    const { start_date, end_date } = resolveRange(tokenStartDate, tokenEndDate);
    try {
      const analyticsData = await getTokenAnalytics(start_date, end_date);
      setTokenAnalytics(analyticsData);
    } catch (error) {
      console.error('Error fetching total analytics:', error);
    }
  };

  useEffect(() => {
    fetchTokenAnalytics();
  }, [tokenStartDate, tokenEndDate, getTokenAnalytics]);

  const handleLlmTokenPeriodChange = useCallback((period) => {
    setLlmTokenStartDate(period?.start_date);
    setLlmTokenEndDate(period?.end_date);
  }, []);

  const handleTokenPeriodChange = useCallback((period) => {
    setTokenStartDate(period?.start_date);
    setTokenEndDate(period?.end_date);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <Table
              title={tableTitle}
              columns={AUDIT_COLUMNS}
              data={auditOperations}
              selectableRowsHighlight
              progressPending={loading}
              paginationPerPage={persistedPageSize}
              onRowClicked={handleRowClicked}
              paginationTotalRows={totalAuditOperations}
              sortIcon={<i className="fa-solid fa-chevron-down"></i>}
              onChangePage={fetchAuditOperations}
              getRowUrl={(row) => `/organizations/${organizationId}/audit-operations/${row.key}`}
              onChangeFilter={handleTypeChange}
              typeSearch="multiselect"
              searchOptions={searchOptions}
            />
          )}
          <div className="d-flex">
            <DateRangeSelector storageKey="audit-operations" onChange={handlePeriodChange} />
            <div style={{ marginLeft: 'auto' }}>
              <Button variant="primary" size="medium" onClick={handleExport}>
                Export JSON
              </Button>
            </div>
          </div>
        </div>
        {hasOrganizationsPermission && tokenAnalytics?.total_tokens != null && (
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 style={{ fontWeight: 'normal' }}>Total Analytics</h2>
            <div className="analytics-card" style={{ maxWidth: '500px' }}>
              <div className="analytics-header">
                <h3>Total Tokens</h3>
              </div>
              <div className="analytics-value">
                {tokenAnalytics.total_tokens.toLocaleString() || 0}
              </div>
              <div className="analytics-details">
                <div className="detail-item">
                  <span className="label">Input tokens</span>
                  <span className="value">
                    {tokenAnalytics.input_tokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Output tokens</span>
                  <span className="value">
                    {tokenAnalytics.output_tokens?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '6vh' }}>
              <DateRangeSelector storageKey="audit-total" onChange={handleTokenPeriodChange} />
            </div>
          </div>
        )}

        {hasOrganizationsPermission && (
          <div className="radius-yes p-2 bg-yes col-12">
            {!tokenAnalyticsByLLM.length ? (
              <p>No LLM analytics data available.</p>
            ) : (
              <Table
                title={tableTokensTitle}
                key={`${llmTokenStartDate}-${llmTokenEndDate}`}
                columns={LLM_COLUMNS}
                data={tokenAnalyticsByLLM}
                selectableRowsHighlight
                paginationPerPage={persistedTokensPageSize}
                paginationTotalRows={totalTokenCount}
                sortIcon={<i className="fa-solid fa-chevron-down" />}
                onChangePage={fetchTokenAnalyticsByLLM}
              />
            )}
            <DateRangeSelector storageKey="audit-llm" onChange={handleLlmTokenPeriodChange} />
          </div>
        )}
      </div>
    </div>
  );
}

function AuditOperationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AuditOperationsApiProvider>
        <AuditOperationsHub canEdit={canEdit} />
      </AuditOperationsApiProvider>
    </ConfirmationProvider>
  );
}

export default AuditOperationsWrapper;
